import React from 'react';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import { Estimate } from '@css-xp/ppc-purchase-experience';
import { ProratedCharges, TaxDetails } from '@css-xp/graphtypes/src/generated-types-sxp';
import { Currency } from '@css-xp/ppc-common';

type QuoteProratedChargesProps = {
  proratedCharges: ProratedCharges;
  lineItemId?: string;
  loading: boolean;
  currency?: string;
};

export const QuoteProratedCharges: React.FC<QuoteProratedChargesProps> = ({
  proratedCharges,
  lineItemId,
  loading,
  currency = Currency.USD,
}) => {
  const taxItems = defaultTo(get(proratedCharges, 'taxItems'), []) as TaxDetails[];
  const partnerDiscount = proratedCharges?.partnerDiscountDetails?.totalPartnerDiscount;
  const partnerDiscountItem = proratedCharges?.partnerDiscountDetails?.partnerDiscounts?.find(
    (discount) => discount?.discountAmount
  );
  return (
    <div className="prorated-charge-line-item" data-testId={`prorated-charge-line-item-${lineItemId}`}>
      <div className="line-item-app-container-selected-product">
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">
            <h4 className="prorated-charge-line-item-title">Increased users in previous billing cycle</h4>
          </p>
          <p className="line-item-app-container-selected-product-pricing-details">
            <Estimate
              currency={currency}
              price={defaultTo(get(proratedCharges, 'total'), 0)}
              loading={loading}
              className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
              data-testid={`quote-lineitem-list-price-${lineItemId}`}
            />
          </p>
        </div>
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">New users</p>
          <p className="line-item-app-container-selected-product-pricing-details">
            {defaultTo(get(proratedCharges, 'users'), 0)}
          </p>
        </div>
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">List price</p>
          <p className="line-item-app-container-selected-product-pricing-details">
            <Estimate
              currency={currency}
              price={defaultTo(get(proratedCharges, 'subTotal'), 0)}
              loading={loading}
              className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
              data-testid={`quote-lineitem-list-price-${lineItemId}`}
            />
          </p>
        </div>
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">Other discounts</p>
          <p className="line-item-app-container-selected-product-pricing-details">
            <Estimate
              currency={currency}
              price={proratedCharges?.otherDiscount ? -proratedCharges.otherDiscount : 0}
              loading={loading}
              className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
              data-testid={`quote-lineitem-list-price-${lineItemId}`}
            />
          </p>
        </div>
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">Credits</p>
          <p className="line-item-app-container-selected-product-pricing-details">
            <Estimate
              currency={currency}
              price={proratedCharges?.credits ? -proratedCharges.credits : 0}
              loading={loading}
              className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
              data-testid={`quote-lineitem-list-price-${lineItemId}`}
            />
          </p>
        </div>
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">{`Partner discount: ${defaultTo(
            partnerDiscountItem?.percentOff,
            0
          )}%`}</p>
          <p className="line-item-app-container-selected-product-pricing-details">
            <Estimate
              currency={currency}
              price={partnerDiscount ? -partnerDiscount : 0}
              loading={loading}
              className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
              data-testid={`quote-lineitem-list-price-${lineItemId}`}
            />
          </p>
        </div>
        <div className="line-item-app-container-selected-product-row-container">
          <p className="line-item-app-container-selected-product-pricing-details">
            <h4 className="line-item-app-container-selected-product-estimate-partner-discount">
              Estimate incl. partner discount
            </h4>
          </p>
          <p className="line-item-app-container-selected-product-pricing-details">
            <Estimate
              currency={currency}
              price={
                defaultTo(proratedCharges.subTotal, 0) -
                defaultTo(proratedCharges.otherDiscount, 0) -
                defaultTo(proratedCharges.credits, 0) -
                defaultTo(proratedCharges?.partnerDiscountDetails?.totalPartnerDiscount, 0)
              }
              loading={loading}
              className={'line-item-app-container-selected-product-estimate-partner-discount-currency'}
              data-testid={`quote-lineitem-list-price-${lineItemId}`}
            />
          </p>
        </div>
        {/* Tax */}
        {taxItems?.map(({ tax, taxAmountLabel, taxPercent }) => {
          return (
            <div
              key={`tax-(${taxPercent}-${taxAmountLabel})`}
              className="line-item-app-container-selected-product-row-container"
            >
              <p className="line-item-app-container-selected-product-pricing-details">{`Tax (${taxPercent}% ${taxAmountLabel})`}</p>
              <Estimate
                currency={currency}
                price={tax as number}
                loading={loading}
                className={'line-item-app-container-selected-product-sub-title-pricing-currency'}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
